import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, ErrorState, FormField, Input, LoadingState, Page, PageHeader, PageSection } from '@/components';
import { useChannels, useSaveWhatsAppChannel } from '@/hooks';
import type { Channel } from '@/services/channelsService';
import { sendTemplateMessage, sendTextMessage } from '@/services/messagesService';
import { listApprovedMetaTemplates } from '@/services/templatesService';
import { getActiveTenantId } from '@/store/tenantSelection';

const initialForm = {
  name: 'WhatsApp principal',
  phone_number_id: '',
  waba_id: '',
  access_token: '',
  verify_token: '',
};

export function Channels() {
  const query = useChannels();
  const saveChannel = useSaveWhatsAppChannel();
  const [editing, setEditing] = useState<Channel | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [testingChannel, setTestingChannel] = useState<Channel | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMode, setTestMode] = useState<'template' | 'text'>('template');
  const [testText, setTestText] = useState('Teste da Central de Mensageria');
  const [testTemplateName, setTestTemplateName] = useState('');
  const [testTemplateLanguage, setTestTemplateLanguage] = useState('');
  const [testTemplateVariables, setTestTemplateVariables] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const activeTenantId = getActiveTenantId();
  const templatesQuery = useQuery({
    queryKey: ['templates', 'meta', 'approved', activeTenantId],
    queryFn: async () => (await listApprovedMetaTemplates()).data,
    enabled: Boolean(testingChannel) && testMode === 'template',
    staleTime: 60 * 1000,
  });
  const approvedTemplates = templatesQuery.data ?? [];
  const selectedTemplate = approvedTemplates.find(
    (template) => template.name === testTemplateName && template.language === testTemplateLanguage
  );
  const selectedTemplateBody = selectedTemplate?.components.find((component) => component.type === 'BODY')?.text ?? '';
  const expectedVariableCount = selectedTemplateBody.match(/\{\{\d+\}\}/g)?.length ?? 0;
  const categories = ['UTILITY', 'MARKETING', 'AUTHENTICATION']
    .map((category) => ({
      category,
      templates: approvedTemplates.filter((template) => template.category === category),
    }))
    .filter((group) => group.templates.length > 0);

  useEffect(() => {
    setTestTemplateVariables('');
  }, [testTemplateLanguage, testTemplateName]);

  useEffect(() => {
    if (!approvedTemplates.length) {
      setTestTemplateName('');
      setTestTemplateLanguage('');
      return;
    }
    const stillAvailable = approvedTemplates.some(
      (template) => template.name === testTemplateName && template.language === testTemplateLanguage
    );
    if (!stillAvailable) {
      setTestTemplateName(approvedTemplates[0].name);
      setTestTemplateLanguage(approvedTemplates[0].language);
    }
  }, [approvedTemplates, testTemplateLanguage, testTemplateName]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setShowForm(true);
  };

  const openEdit = (channel: Channel) => {
    setEditing(channel);
    setForm({
      name: channel.name,
      phone_number_id: channel.phone_number_id ?? '',
      waba_id: channel.waba_id ?? '',
      access_token: '',
      verify_token: '',
    });
    setShowForm(true);
  };

  const handleTest = async () => {
    if (!testingChannel || !testPhone) return;
    if (testMode === 'template' && (!testTemplateName || !testTemplateLanguage)) return;
    if (testMode === 'text' && !testText) return;
    setSendingTest(true);
    try {
      const tracking = {
        channel_id: testingChannel.id,
        idempotency_key: `manual-test-${testingChannel.id}-${Date.now()}`,
        source_system: 'central_mensageria',
        source_type: 'manual_test',
        source_id: testingChannel.id,
      };

      if (testMode === 'template') {
        const values = testTemplateVariables.split(',').map((value) => value.trim()).filter(Boolean);
        if (values.some((value) => value.startsWith('EA') && value.length >= 50)) {
          setTestTemplateVariables('');
          toast.error('Access Token não pode ser usado como variável. Revogue o token exposto e gere outro na Meta.');
          return;
        }
        if (values.length !== expectedVariableCount) {
          toast.error(`Este template espera ${expectedVariableCount} variável(is), mas foram informadas ${values.length}.`);
          return;
        }
        const variables = Object.fromEntries(values.map((value, index) => [String(index + 1), value]));
        await sendTemplateMessage({
          to: testPhone,
          type: 'template',
          ...tracking,
          template: {
            template_id: testTemplateName,
            language: testTemplateLanguage,
            variables: Object.keys(variables).length ? variables : undefined,
          },
        });
      } else {
        await sendTextMessage({
          to: testPhone,
          type: 'text',
          text: testText,
          ...tracking,
        });
      }
      toast.success(testMode === 'template' ? 'Template enviado' : 'Mensagem de teste enviada');
      setTestingChannel(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao enviar mensagem de teste');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSave = async () => {
    if (form.access_token && (form.access_token.length < 50 || !form.access_token.startsWith('EA'))) {
      toast.error(`Access Token inválido: recebido com ${form.access_token.length} caracteres. Cole o token completo iniciado por EA.`);
      return;
    }

    try {
      await saveChannel.mutateAsync({
        channelId: editing?.id,
        payload: {
          name: form.name,
          phone_number_id: form.phone_number_id || undefined,
          waba_id: form.waba_id || undefined,
          access_token: form.access_token || undefined,
          verify_token: form.verify_token || undefined,
          is_default: true,
        },
      });
      toast.success(editing ? 'WhatsApp atualizado' : 'WhatsApp configurado');
      setShowForm(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível salvar o WhatsApp');
    }
  };

  return (
    <Page>
      <PageHeader
        title="WhatsApps do projeto"
        description="Cada projeto usa seu próprio número, credenciais, templates e histórico."
        right={<Button variant="secondary" onClick={openCreate}>Adicionar WhatsApp</Button>}
      />

      {showForm && (
        <PageSection>
          <Card>
            <CardHeader>
              <div className="font-semibold">{editing ? 'Editar WhatsApp' : 'Conectar WhatsApp'}</div>
            </CardHeader>
            <CardBody>
              <div className="flow-grid-2">
                <FormField label="Nome do canal">
                  <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
                </FormField>
                <FormField label="Phone Number ID">
                  <Input value={form.phone_number_id} onChange={(event) => setForm({ ...form, phone_number_id: event.target.value })} />
                </FormField>
                <FormField label="WABA ID">
                  <Input value={form.waba_id} onChange={(event) => setForm({ ...form, waba_id: event.target.value })} />
                </FormField>
                <FormField label={editing ? 'Novo Access Token Meta (opcional)' : 'Access Token Meta'}>
                  <textarea
                    className="textarea"
                    name="meta_access_token_new"
                    autoComplete="off"
                    spellCheck={false}
                    rows={3}
                    placeholder="Cole o token completo iniciado por EA"
                    value={form.access_token}
                    onChange={(event) => setForm({ ...form, access_token: event.target.value.replace(/\s/g, '') })}
                  />
                  <div className={form.access_token && (form.access_token.length < 50 || !form.access_token.startsWith('EA')) ? 'text-sm text-red-600' : 'text-sm text-gray-600'}>
                    {form.access_token.length} caracteres · deve iniciar com EA
                  </div>
                </FormField>
                <FormField label="Verify Token do webhook">
                  <Input
                    type="text"
                    name="webhook_verify_token"
                    autoComplete="off"
                    value={form.verify_token}
                    onChange={(event) => setForm({ ...form, verify_token: event.target.value })}
                  />
                </FormField>
              </div>
              <div className="flow-actions">
                <Button onClick={() => void handleSave()} disabled={saveChannel.isLoading || !form.name}>Salvar</Button>
                <Button variant="secondary" onClick={() => setShowForm(false)}>Voltar</Button>
              </div>
            </CardBody>
          </Card>
        </PageSection>
      )}

      {testingChannel && (
        <PageSection>
          <Card>
            <CardHeader>
              <div className="font-semibold">Enviar teste por {testingChannel.name}</div>
            </CardHeader>
            <CardBody>
              <div className="flow-grid-2">
                <FormField label="WhatsApp destinatário com DDI">
                  <Input placeholder="5541999999999" value={testPhone} onChange={(event) => setTestPhone(event.target.value)} />
                </FormField>
                <FormField label="Tipo de envio">
                  <select className="select" value={testMode} onChange={(event) => setTestMode(event.target.value as 'template' | 'text')}>
                    <option value="template">Template aprovado</option>
                    <option value="text">Texto livre (janela de 24h)</option>
                  </select>
                </FormField>
                {testMode === 'template' ? (
                  <>
                    <FormField label="Template aprovado na Meta">
                      <select
                        className="select"
                        value={selectedTemplate ? `${selectedTemplate.name}|${selectedTemplate.language}` : ''}
                        onChange={(event) => {
                          const separator = event.target.value.lastIndexOf('|');
                          setTestTemplateName(event.target.value.slice(0, separator));
                          setTestTemplateLanguage(event.target.value.slice(separator + 1));
                        }}
                        disabled={templatesQuery.isLoading || approvedTemplates.length === 0}
                      >
                        {templatesQuery.isLoading && <option value="">Carregando templates aprovados...</option>}
                        {!templatesQuery.isLoading && approvedTemplates.length === 0 && <option value="">Nenhum template aprovado encontrado</option>}
                        {categories.map((group) => (
                          <optgroup key={group.category} label={group.category}>
                            {group.templates.map((template) => (
                              <option key={`${template.id}-${template.language}`} value={`${template.name}|${template.language}`}>
                                {template.name} · {template.language} · {template.category}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      {templatesQuery.isError && (
                        <div>
                          <div className="text-sm text-red-600">{templatesQuery.error instanceof Error ? templatesQuery.error.message : 'Falha ao carregar templates da Meta'}</div>
                          <Button variant="secondary" onClick={() => void templatesQuery.refetch()}>Tentar novamente</Button>
                        </div>
                      )}
                    </FormField>
                    <FormField label="Categoria e idioma">
                      <Input readOnly value={selectedTemplate ? `${selectedTemplate.category} · ${selectedTemplate.language}` : ''} />
                    </FormField>
                    {expectedVariableCount > 0 ? (
                      <FormField label={`${expectedVariableCount} variável(is) na ordem, separadas por vírgula`}>
                        <Input
                          placeholder="João, Pedido 123"
                          value={testTemplateVariables}
                          onChange={(event) => setTestTemplateVariables(event.target.value)}
                        />
                      </FormField>
                    ) : (
                      <FormField label="Variáveis">
                        <Input readOnly value="Este template não possui variáveis" />
                      </FormField>
                    )}
                    {selectedTemplate && (
                      <FormField label="Prévia do corpo">
                        <div className="code-block">
                          {selectedTemplate.components.find((component) => component.type === 'BODY')?.text || 'Template sem corpo textual'}
                        </div>
                      </FormField>
                    )}
                  </>
                ) : (
                  <FormField label="Mensagem">
                    <Input value={testText} onChange={(event) => setTestText(event.target.value)} />
                  </FormField>
                )}
              </div>
              <div className="flow-actions">
                <Button
                  onClick={() => void handleTest()}
                  disabled={sendingTest || !testPhone || (testMode === 'template' ? !testTemplateName || !testTemplateLanguage : !testText)}
                >
                  {testMode === 'template' ? 'Enviar template' : 'Enviar teste'}
                </Button>
                <Button variant="secondary" onClick={() => setTestingChannel(null)}>Voltar</Button>
              </div>
            </CardBody>
          </Card>
        </PageSection>
      )}

      <PageSection>
        {query.isLoading ? (
          <LoadingState title="Carregando canais" description="Buscando WhatsApps configurados para o projeto." />
        ) : query.isError ? (
          <ErrorState title="Falha ao carregar canais" onRetry={() => query.refetch()} />
        ) : query.data?.length ? (
          <div className="content-grid">
            {query.data.map((channel) => (
              <Card key={channel.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{channel.name}</div>
                    <Badge variant={channel.status === 'connected' ? 'success' : channel.status === 'error' ? 'error' : 'warning'}>
                      {channel.status === 'connected' ? 'Conectado' : channel.status === 'error' ? 'Com erro' : 'Aguardando configuração'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="text-sm text-gray-600">Phone Number ID: {channel.phone_number_id || 'não informado'}</div>
                  <div className="text-sm text-gray-600">WABA ID: {channel.waba_id || 'não informado'}</div>
                  <div className="flow-actions">
                    <Button variant="secondary" onClick={() => openEdit(channel)}>Configurar</Button>
                    <Button
                      onClick={() => setTestingChannel(channel)}
                      disabled={channel.status !== 'connected'}
                    >
                      Enviar teste
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Nenhum WhatsApp conectado"
            description="Cadastre o WhatsApp deste projeto para testar mensagens e automações separadamente."
            action={<Button variant="secondary" onClick={openCreate}>Conectar WhatsApp</Button>}
          />
        )}
      </PageSection>
    </Page>
  );
}
